import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { MessagesHttpService } from './messages/messages-http.service';

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private currentToken = '';
  private initialized = false;

  constructor(
    private messagesHttp: MessagesHttpService,
    private router: Router
  ) {}

  async init(): Promise<void> {
    console.log('[FCM] Init called. isNative:', Capacitor.isNativePlatform(), 'initialized:', this.initialized);

    if (!Capacitor.isNativePlatform()) {
      console.log('[FCM] Not a native platform, skipping');
      return;
    }

    if (this.initialized) {
      console.log('[FCM] Already initialized, skipping');
      return;
    }

    console.log('[FCM] Checking current permission status...');
    const currentPermission = await PushNotifications.checkPermissions();
    console.log('[FCM] Current permission:', currentPermission);

    console.log('[FCM] Requesting notification permissions...');
    const permission = await PushNotifications.requestPermissions();
    console.log('[FCM] Permission result:', permission);

    if (permission.receive !== 'granted') {
      console.warn('[FCM] ⚠️ Notification permission denied:', permission.receive);
      return;
    }

    console.log('[FCM] ✅ Permission granted, registering for push notifications...');
    await PushNotifications.register();

    PushNotifications.addListener('registration', (token) => {
      console.log('[FCM] Token received:', token.value.substring(0, 20) + '...');
      this.currentToken = token.value;
      this.messagesHttp.registerDeviceToken(token.value, Capacitor.getPlatform()).subscribe({
        next: () => console.log('[FCM] ✅ Token registered with backend'),
        error: (err) => console.error('[FCM] ❌ Failed to register token:', err)
      });
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error:', err);
    });

    // Native H2SMessagingService handles notification display (with reply buttons).
    // This listener handles taps on notifications that open the app.
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification.data;
      if (data?.conversation_id) {
        this.router.navigate(['/messages', 'chat'], {
          queryParams: { conversationId: data.conversation_id }
        });
      } else if (data?.type === 'feed_post' && data?.post_id) {
        this.router.navigate(['/messages/feeds/post', data.post_id]);
      } else if (data?.type === 'feed_reply' && data?.post_id) {
        this.router.navigate(['/messages/feeds/post', data.post_id]);
      } else if (data?.event === 'new_report') {
        this.router.navigate(['/messages/reports']);
      } else if (data?.type === 'payment_failed') {
        // Surface the failed payment on the dashboard's "Needs attention" card.
        this.router.navigate(['/dashboard']);
      } else if (data?.type === 'kid_absence' && data?.trip_id) {
        this.router.navigate(['/trips', 'detail'], { queryParams: { id: data.trip_id, from: 'push' } });
      }
    });

    this.initialized = true;
  }

  async cleanup(): Promise<void> {
    if (!Capacitor.isNativePlatform() || !this.currentToken) return;

    this.messagesHttp.removeDeviceToken(this.currentToken).subscribe({
      error: (err) => console.error('Failed to remove FCM token:', err)
    });
    this.currentToken = '';
    this.initialized = false;
    await PushNotifications.removeAllListeners();
  }
}
